// UnderConstruction.tsx
import React from "react";
import ConstructionIcon from "@mui/icons-material/Construction";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface UnderConstructionPageProps {
    title?: string;
    message?: string;
    actionLabel?: string;
    onAction?: () => void;
    homeHref?: string;
}

interface UnderConstructionPageState {
    mounted: boolean;
    dark: boolean;
    step: number;
}

export default class UnderConstructionPage extends React.Component<
    UnderConstructionPageProps,
    UnderConstructionPageState
> {
    private tick?: number;

    constructor(props: UnderConstructionPageProps) {
        super(props);
        this.state = {
            mounted: false,
            dark: false,
            step: 0,
        };
    }

    componentDidMount() {
        requestAnimationFrame(() => this.setState({ mounted: true }));

        const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
        this.setState({ dark: !!mq?.matches });
        mq?.addEventListener?.("change", (e) => this.setState({ dark: e.matches }));

        this.tick = window.setInterval(() => {
            this.setState((s) => ({ step: (s.step + 1) % 4 }));
        }, 450);
    }

    componentWillUnmount() {
        if (this.tick) window.clearInterval(this.tick);
    }

    private get accent() {
        return "#c79656";
    }

    private ring(focused: boolean) {
        return focused ? "0 0 0 2px rgba(199,150,86,.28)" : "none";
    }

    private handleAction = () => {
        const { onAction, homeHref } = this.props;
        if (onAction) return onAction();
        if (homeHref) return window.location.assign(homeHref);
        window.history.back();
    };

    render() {
        const accent = this.accent;
        const isDark = this.state.dark;

        const container: React.CSSProperties = {
            minHeight: "100vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            boxSizing: "border-box",
            transition: "opacity 180ms ease-out, transform 180ms ease-out",
            opacity: this.state.mounted ? 1 : 0,
        };

        const card: React.CSSProperties = {
            width: "min(720px, 96vw)",
            borderRadius: 20,
            border: "1px solid rgba(199,150,86,.35)",
            background: isDark ? "#141414" : "#ffffff",
            color: isDark ? "#f5f5f5" : "#111111",
            boxShadow: "0 18px 64px rgba(0,0,0,.35)",
            padding: 36,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 24,
        };

        const headerRow: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
        };

        const pill: React.CSSProperties = {
            alignSelf: "center",
            fontSize: 12,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: isDark ? "#f5f5f5" : "#111111",
            background: isDark ? "rgba(199,150,86,.18)" : "rgba(199,150,86,.16)",
            border: "1px solid rgba(199,150,86,.35)",
            padding: "6px 10px",
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
        };

        const title: React.CSSProperties = {
            margin: 0,
            textAlign: "center",
            fontSize: "1.5rem",
            lineHeight: 1.25,
        };

        const subtitle: React.CSSProperties = {
            margin: 0,
            textAlign: "center",
            opacity: 0.9,
            lineHeight: 1.6,
            fontSize: 15,
        };

        const progressRow: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
        };

        const dot = (i: number): React.CSSProperties => ({
            width: 8,
            height: 8,
            borderRadius: 8,
            background: i === this.state.step ? accent : (isDark ? "rgba(255,255,255,.18)" : "rgba(0,0,0,.12)"),
            transform: i === this.state.step ? "translateY(-2px)" : "translateY(0)",
            transition: "all 160ms ease",
        });

        const actionsRow: React.CSSProperties = {
            display: "flex",
            justifyContent: "center",
            marginTop: 6,
        };

        const actionBtn: React.CSSProperties = {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            border: "1px solid rgba(199,150,86,.45)",
            background: accent,
            color: "#fff",
            borderRadius: 14,
            padding: "12px 18px",
            fontWeight: 600,
            cursor: "pointer",
            outline: "none",
            boxShadow: this.ring(false),
        };

        const iconMainStyle = { color: accent, fontSize: 42 } as const;
        const iconSmallStyle = { color: isDark ? "#eaeaea" : "#333", fontSize: 18 } as const;

        const titleText = this.props.title ?? "This page is under construction";
        const messageText =
            this.props.message ??
            "We're busy building something great here. Please come back soon!";

        return (
            <div style={container}>
                <div style={card} role="region" aria-label="Under construction">
                    <div style={pill}>
                        <HourglassBottomIcon style={iconSmallStyle} />
                        In Progress
                    </div>

                    <div style={headerRow}>
                        <ConstructionIcon style={iconMainStyle} />
                        <h1 style={title}>{titleText}</h1>
                    </div>

                    <p style={subtitle}>{messageText}</p>

                    <div style={progressRow} aria-hidden="true">
                        <div style={dot(0)} />
                        <div style={dot(1)} />
                        <div style={dot(2)} />
                        <div style={dot(3)} />
                    </div>

                    <div style={actionsRow}>
                        <button
                            type="button"
                            onClick={this.handleAction}
                            style={actionBtn}
                            aria-label={this.props.actionLabel ?? "Go back"}
                        >
                            <ArrowBackIcon style={{ color: "#fff", fontSize: 18 }} />
                            {this.props.actionLabel ?? "Back"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
